import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Trash2, Plus, Mail, Printer, KeyRound, Cake, CalendarPlus, CalendarCheck, Phone, AtSign, Layers, CalendarDays, Info } from "lucide-react";
import { Layout } from "../../components/layout/Layout";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { NativeSelect, ProgressBar, EmptyState, ConfirmDialog, Field } from "../../components/common";
import { FicheModules } from "../../components/FicheModules";
import { FicheSessions } from "../../components/FicheSessions";
import { UserFormDialog } from "../../components/UserFormDialog";
import { EleveFormDialog } from "./EleveListPage";
import { api, formatApiError } from "../../lib/api";
import { fmtDate, age, initials, fmtDuration, todayISO } from "../../lib/format";
import { useAuth, hasPerm } from "../../context/AuthContext";

function InfoLine({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5 text-sm">
      <Icon size={15} className="text-muted-foreground shrink-0" />
      <span className="text-muted-foreground">{label}</span>
      <span className="ml-auto font-medium text-right">{value || "—"}</span>
    </div>
  );
}

export function FicheHeader({ fiche }) {
  return (
    <div className="flex items-center gap-4">
      <span className="h-14 w-14 shrink-0 rounded-2xl bg-primary/10 text-primary text-xl font-semibold flex items-center justify-center">{initials(fiche.prenom, fiche.nom)}</span>
      <div className="min-w-0">
        <h2 className="font-heading text-2xl font-semibold truncate">{fiche.prenom} {fiche.nom}</h2>
        <p className="text-sm text-muted-foreground">{fiche.date_naissance ? `${age(fiche.date_naissance)} ans` : "Âge non renseigné"}{fiche.date_debut_formation ? ` · en formation depuis le ${fmtDate(fiche.date_debut_formation)}` : ""}</p>
      </div>
    </div>
  );
}

export function FicheIdentity({ fiche }) {
  return (
    <div className="space-y-2.5">
      <InfoLine icon={Cake} label="Date de naissance" value={fmtDate(fiche.date_naissance)} />
      <InfoLine icon={CalendarPlus} label="Inscription" value={fmtDate(fiche.date_inscription)} />
      <InfoLine icon={CalendarCheck} label="Début de formation" value={fmtDate(fiche.date_debut_formation)} />
      {fiche.date_fin_formation && <InfoLine icon={CalendarCheck} label="Fin de formation" value={fmtDate(fiche.date_fin_formation)} />}
      <InfoLine icon={AtSign} label="E-mail" value={fiche.email} />
      <InfoLine icon={Phone} label="Téléphone" value={fiche.telephone} />
    </div>
  );
}

export function ProgressSummary({ fiche }) {
  const p = fiche.progress;
  return (
    <div>
      <div className="flex items-end justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Progression</p>
        <p className="font-heading text-2xl font-semibold tabular-nums">{p.percent} %</p>
      </div>
      <ProgressBar percent={p.percent} className="mt-2" />
      <p className="text-xs text-muted-foreground mt-2">{p.done}/{p.total} activités · {p.modules_termines}/{p.modules} modules terminés</p>
    </div>
  );
}

export default function EleveDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = hasPerm(user, "eleves", "edit");
  const canEval = hasPerm(user, "eleves", "evaluer");
  const canDelete = hasPerm(user, "eleves", "delete");
  const [fiche, setFiche] = useState(null);
  const [error, setError] = useState("");
  const [modules, setModules] = useState([]);
  const [toAdd, setToAdd] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const [mailTo, setMailTo] = useState("");
  const [mailMsg, setMailMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [tab, setTab] = useState("modules");

  const load = () => api.get(`/eleves/${id}`).then(({ data }) => setFiche(data)).catch((e) => setError(formatApiError(e.response?.data?.detail)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);
  useEffect(() => { if (canEval) api.get("/modules").then(({ data }) => setModules(data)).catch(() => {}); }, [canEval]);

  const available = useMemo(() => {
    const on = new Set((fiche?.modules || []).map((m) => m.module_id));
    return modules.filter((m) => !on.has(m.id));
  }, [modules, fiche]);

  if (error) return <Layout title="Fiche élève"><p className="text-destructive">{error}</p></Layout>;
  if (!fiche) return <Layout title="Fiche élève"><p className="text-muted-foreground">Chargement...</p></Layout>;

  const addModule = async () => {
    if (!toAdd) return;
    try {
      const { data } = await api.post(`/eleves/${id}/modules`, { module_id: toAdd });
      setFiche(data);
      setToAdd("");
      toast.success("Module ajouté à la fiche");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const onModule = (em) => {
    setFiche((f) => {
      const mods = f.modules.map((m) => (m.id === em.id ? em : m));
      const total = mods.reduce((s, m) => s + m.progress.total, 0);
      const done = mods.reduce((s, m) => s + m.progress.done, 0);
      return { ...f, modules: mods, progress: { ...f.progress, total, done, percent: total ? Math.round((100 * done) / total) : 0, modules_termines: mods.filter((m) => m.termine).length } };
    });
  };

  const removeModule = async (em) => {
    try {
      const { data } = await api.delete(`/eleves/${id}/modules/${em.id}`);
      setFiche(data);
      toast.success("Module retiré de la fiche");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sendMail = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/eleves/${id}/send-suivi`, { to_email: mailTo, message: mailMsg });
      toast.success(`Fiche de suivi envoyée à ${data.to_email}`);
      setMailOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/eleves/${id}`);
      toast.success("Fiche élève supprimée");
      navigate("/eleves");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const acc = fiche.account;
  const accState = acc ? (!acc.active ? "Désactivé" : acc.access_end && acc.access_end < todayISO() ? "Expiré" : acc.access_start && acc.access_start > todayISO() ? "À venir" : "Actif") : null;

  return (
    <Layout title="Fiche élève">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4 no-print">
        <Link to="/eleves" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={15} /> Tous les élèves</Link>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()}><Printer size={14} /> Imprimer</Button>
          <Button variant="outline" size="sm" onClick={() => { setMailTo(fiche.email || ""); setMailMsg(""); setMailOpen(true); }} data-testid="fiche-send-mail"><Mail size={14} /> Envoyer par e-mail</Button>
          {canEdit && <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="fiche-edit"><Pencil size={14} /> Modifier</Button>}
          {canDelete && <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => setConfirmDelete(true)}><Trash2 size={14} /></Button>}
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[320px_1fr]">
        <aside className="space-y-4">
          <section className="rounded-xl border border-border bg-card p-5 space-y-5">
            <FicheHeader fiche={fiche} />
            <ProgressSummary fiche={fiche} />
            <FicheIdentity fiche={fiche} />
            {fiche.notes && <p className="text-sm rounded-lg bg-muted px-3 py-2 whitespace-pre-line"><Info size={13} className="inline mr-1 -mt-0.5" />{fiche.notes}</p>}
          </section>
          <section className="rounded-xl border border-border bg-card p-5 no-print">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><KeyRound size={13} /> Accès élève</p>
            {acc ? (
              <div className="space-y-1 text-sm">
                <p>Matricule <code className="font-semibold">{acc.matricule}</code> <span className={`ml-1 rounded-full px-2 py-0.5 text-[11px] ${accState === "Actif" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "bg-muted text-muted-foreground"}`}>{accState}</span></p>
                <p className="text-muted-foreground text-xs">Du {fmtDate(acc.access_start)} au {fmtDate(acc.access_end)}</p>
                {user.is_admin && <Link to="/admin" className="text-xs text-primary hover:underline">Gérer dans Administration</Link>}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Pas encore d'accès : l'élève ne peut pas consulter son planning ni son suivi.</p>
                {user.is_admin && <Button size="sm" variant="outline" onClick={() => setAccessOpen(true)} data-testid="fiche-create-access"><KeyRound size={14} /> Créer un accès élève</Button>}
              </div>
            )}
          </section>
        </aside>

        <div className="min-w-0">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="no-print">
              <TabsTrigger value="modules" className="gap-1.5"><Layers size={14} /> Modules & notes ({fiche.modules.length})</TabsTrigger>
              <TabsTrigger value="planning" className="gap-1.5" data-testid="fiche-tab-planning"><CalendarDays size={14} /> Planning ({fiche.sessions?.length || 0})</TabsTrigger>
            </TabsList>
            <TabsContent value="modules" className="mt-4 space-y-4">
              {canEval && (
                <div className="flex flex-wrap gap-2 rounded-xl border border-dashed border-border p-3 no-print">
                  <NativeSelect value={toAdd} onChange={(e) => setToAdd(e.target.value)} className="flex-1 min-w-[220px]" data-testid="fiche-module-select">
                    <option value="">{available.length ? "— Sélectionner un module à ajouter —" : "Tous les modules sont déjà sur la fiche"}</option>
                    {available.map((m) => <option key={m.id} value={m.id}>{m.nom} · {m.activities.length} activité{m.activities.length > 1 ? "s" : ""}{m.duree_minutes ? ` · ${fmtDuration(m.duree_minutes)}` : ""}</option>)}
                  </NativeSelect>
                  <Button onClick={addModule} disabled={!toAdd} data-testid="fiche-module-add"><Plus size={15} /> Ajouter à la fiche</Button>
                </div>
              )}
              {fiche.modules.length === 0 ? (
                <div className="rounded-xl border border-border bg-card"><EmptyState icon={Layers} title="Aucun module sur cette fiche" text="Sélectionnez un module dans la liste pour suivre les activités réalisées par l'élève et les noter." /></div>
              ) : (
                <FicheModules eleveId={id} modules={fiche.modules} readOnly={!canEval} onModule={onModule} onRemove={canEval ? removeModule : null} />
              )}
            </TabsContent>
            <TabsContent value="planning" className="mt-4">
              <FicheSessions sessions={fiche.sessions} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <EleveFormDialog open={editOpen} onOpenChange={setEditOpen} initial={fiche} onSaved={setFiche} />
      <UserFormDialog open={accessOpen} onOpenChange={setAccessOpen} preset={{ user_type: "eleve", eleve_id: fiche.id, nom: fiche.nom, prenom: fiche.prenom, email: fiche.email || "", telephone: fiche.telephone || "", ...(fiche.date_fin_formation ? { access_end: fiche.date_fin_formation } : {}) }} onSaved={() => load()} />
      <ConfirmDialog open={confirmDelete} onOpenChange={setConfirmDelete} title="Supprimer cette fiche élève ?" description="Les modules, notes et inscriptions aux cours de cet élève seront supprimés, ainsi que son accès élève éventuel. Action irréversible." confirmLabel="Supprimer définitivement" onConfirm={remove} />
      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Envoyer la fiche de suivi</DialogTitle>
            <DialogDescription>Progression, activités réalisées, notes et prochains cours.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Destinataire"><Input type="email" value={mailTo} onChange={(e) => setMailTo(e.target.value)} placeholder="eleve@exemple.fr" /></Field>
            <Field label="Message (facultatif)"><Textarea rows={3} value={mailMsg} onChange={(e) => setMailMsg(e.target.value)} /></Field>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMailOpen(false)}>Annuler</Button>
            <Button onClick={sendMail} disabled={sending || !mailTo}><Mail size={14} /> Envoyer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
